import { keyframes } from "@emotion/react";

import { getThemeValue } from "@theme/components";

import type { ThemeUIStyleObject } from "@theme/components";

// For text elements, focus will add more space
const focusAnimationText = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-4px", opacity: 1 },
});

// For non text elements, focus will have tighter box around it
const focusAnimationBox = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-3px", opacity: 1 },
});

const linkSmStyles: ThemeUIStyleObject = {
  fontSize: "fs12",
  lineHeight: "lh300",
};

export default {
  cursor: "pointer",
  position: "relative",
  fontFamily: "textSans",
  fontSize: "inherit",
  textDecoration: "none",
  lineHeight: "lh400",
  letterSpacing: "tight100",
  color: "textPrimary",
  transition: "color 0.2s ease-in-out",
  "&:hover": {
    color: "textLevel2",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textLevel2",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
      width: "100%",
    },
  },
  "&:active": {
    color: "textBrand",
  },
} as ThemeUIStyleObject;

/*
 * Link Style Variants
 */

export const override: ThemeUIStyleObject = {};

export const link: ThemeUIStyleObject = {
  variant: "links.default",
};

export const linkDark: ThemeUIStyleObject = {
  variant: "links.default",
  color: "textWhiteStatic",
  "&:hover": {
    color: "grey200",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "grey200",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const linkMedium: ThemeUIStyleObject = {
  variant: "links.default",
  fontWeight: "500",
};

export const linkMediumDark: ThemeUIStyleObject = {
  variant: "links.linkDark",
  fontWeight: "500",
};

export const linkSm: ThemeUIStyleObject = {
  variant: "links.default",
  ...linkSmStyles,
};

export const linkDarkSm: ThemeUIStyleObject = {
  variant: "links.default",
  color: "textWhiteStatic",
  ...linkSmStyles,
  "&:hover": {
    color: "grey200",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "grey200",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const linkMediumSm: ThemeUIStyleObject = {
  variant: "links.linkSm",
  fontWeight: "500",
  ...linkSmStyles,
};

export const linkMediumDarkSm: ThemeUIStyleObject = {
  variant: "links.linkDarkSm",
  fontWeight: "500",
};

/*
 * Link Gold Variant
 */
export const linkGold: ThemeUIStyleObject = {
  variant: "links.default",
  color: "textBrand",
  "&:hover": {
    color: "textPrimary",
  },
  "&:active": {
    color: "textPrimary",
  },
};

export const linkGoldMedium: ThemeUIStyleObject = {
  variant: "links.linkGold",
  fontWeight: "500",
};

/*
 * Text Link Style Variants
 */

export const textLink: ThemeUIStyleObject = {
  variant: "links.default",
  textDecoration: "underline",
  "&:hover": {
    color: "grey200",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textLevel2",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
  "&:active": {
    color: "textBrand",
  },
};

export const textLinkMedium: ThemeUIStyleObject = {
  variant: "links.textLink",
  fontWeight: "500",
};

/*
 * Text Link Primary
 */

export const textLinkPrimary: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "textPrimary",
  "&:hover": {
    color: "textLevel2",
  },
};

export const textLinkPrimaryDark: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "textWhiteStatic",
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textWhiteStatic",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkPrimaryMedium: ThemeUIStyleObject = {
  variant: "links.textLinkPrimary",
  fontWeight: "500",
};

export const textLinkPrimaryMediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkPrimaryDark",
  fontWeight: "500",
};

/*
 * Text Link Level 2
 */

export const textLinkLevel2: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "textLevel2",
  "&:hover": {
    color: "textPrimary",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textPrimary",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkLevel2Dark: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "grey200",
  "&:hover": {
    color: "textWhiteStatic",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textWhiteStatic",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkLevel2Medium: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2",
  fontWeight: "500",
};

export const textLinkLevel2MediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2Dark",
  fontWeight: "500",
};

/*
 * Text Link Level 3
 */

export const textLinkLevel3: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "textLevel3",
  "&:hover": {
    color: "textPrimary",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textPrimary",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkLevel3Dark: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "grey300",
  "&:hover": {
    color: "textWhiteStatic",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textWhiteStatic",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkLevel3Medium: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3",
  fontWeight: "500",
};

export const textLinkLevel3MediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3Dark",
  fontWeight: "500",
};

/*
 * Text Link Primary
 */

export const textLinkGold: ThemeUIStyleObject = {
  variant: "links.textLink",
  color: "textBrandLevel2",
  "&:hover": {
    color: "textPrimary",
  },
  "&:active": {
    color: "textPrimary",
  },
};

export const textLinkGoldDark: ThemeUIStyleObject = {
  variant: "links.textLinkGold",
  "&:hover": {
    color: "textWhiteStatic",
  },
  "&:active": {
    color: "textWhiteStatic",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textWhiteStatic",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

export const textLinkGoldMedium: ThemeUIStyleObject = {
  variant: "links.textLinkGold",
  fontWeight: "500",
};

export const textLinkGoldMediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkGold",
  fontWeight: "500",
  "&:hover": {
    color: "textWhiteStatic",
  },
  "&:active": {
    color: "textWhiteStatic",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "textWhiteStatic",
    textDecoration: "none",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationText} 0.1s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

const buttonOverrides: ThemeUIStyleObject = {
  span: { fontSize: "inherit", lineHeight: "inherit" },
};

// and also export all button variants
export const primaryButton: ThemeUIStyleObject = { variant: "buttons.primary", ...buttonOverrides };
export const primarySmButton: ThemeUIStyleObject = {
  variant: "buttons.primarySm",
  ...buttonOverrides,
};
export const primaryDarkButton: ThemeUIStyleObject = {
  variant: "buttons.primaryDark",
  ...buttonOverrides,
};
export const primaryDarkSmButton: ThemeUIStyleObject = {
  variant: "buttons.primaryDarkSm",
  ...buttonOverrides,
};

export const outlinedButton: ThemeUIStyleObject = {
  variant: "buttons.outlined",
  ...buttonOverrides,
};
export const outlinedSmButton: ThemeUIStyleObject = {
  variant: "buttons.outlinedSm",
  ...buttonOverrides,
};
export const outlinedDarkButton: ThemeUIStyleObject = {
  variant: "buttons.outlinedDark",
  ...buttonOverrides,
};
export const outlinedDarkSmButton: ThemeUIStyleObject = {
  variant: "buttons.outlinedDarkSm",
  ...buttonOverrides,
};

export const neutralButton: ThemeUIStyleObject = { variant: "buttons.neutral", ...buttonOverrides };
export const neutralSmButton: ThemeUIStyleObject = {
  variant: "buttons.neutralSm",
  ...buttonOverrides,
};
export const neutralDarkButton: ThemeUIStyleObject = {
  variant: "buttons.neutralDark",
  ...buttonOverrides,
};
export const neutralDarkSmButton: ThemeUIStyleObject = {
  variant: "buttons.neutralDarkSm",
  ...buttonOverrides,
};

export const iconButton: ThemeUIStyleObject = {
  variant: "buttons.icon",
  display: "inline-block",
};

export const iconGoldButton: ThemeUIStyleObject = {
  variant: "buttons.iconGold",
  display: "inline-block",
};

export const iconDarkButton: ThemeUIStyleObject = {
  variant: "buttons.iconDark",
  display: "inline-block",
};

export const selectButton: ThemeUIStyleObject = {
  variant: "buttons.select",
  textDecoration: "none",
  ...buttonOverrides,
};

/*
 * Miscelaneous Link Variants
 */

export const box: ThemeUIStyleObject = {
  variant: "links.default",
  display: "inline-block",
  "&:hover": {
    color: "textBrand",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "none",
      animation: `${focusAnimationBox} 0.1s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldPrimary")}`,
    },
  },
};

export const navbarItem: ThemeUIStyleObject = {
  variant: "links.box",
  // Needed for focus ring
  height: (t) => `calc(100% - ${getThemeValue(t, "space.s200")})`,
  display: "flex",
  my: "s100",
  alignItems: "center",
  span: {
    transition: "color 0.3s ease-in-out",
    position: "relative",
    fontFamily: "textSans",
    fontSize: "fs14",
    textDecoration: "none",
    lineHeight: "lh100",
    color: "white",
  },
  "&:hover span": {
    color: "grey200",
  },
};

export const navbarDropdownItem: ThemeUIStyleObject = {
  variant: "links.box",
  display: "flex",
  my: "s100",
  alignItems: "center",
  transition: "color 0.3s ease-in-out",
  fontFamily: "textSans",
  fontSize: "fs14",
  textDecoration: "none",
  lineHeight: "lh100",
  "&:hover": {
    borderLeft: (t) => `2px solid ${getThemeValue(t, "colors.grey300")}`,
  },
  "&:hover span": {
    color: "textLevel2",
  },
};

export const accountNavItem: ThemeUIStyleObject = {
  variant: "links.textLinkPrimaryDark",
  fontSize: "fs12",
  textTransform: "uppercase",
  letterSpacing: "loose100",
  textDecoration: "none",
  px: "s400",
};

export const arrow: ThemeUIStyleObject = {
  variant: "links.default",
  color: "textWhiteStatic",
  display: "flex",
  alignItems: "center",
  gap: "s100",
  whiteSpace: "nowrap",
  "&:hover": {
    color: "grey200",
  },
};

export const trailingIcon: ThemeUIStyleObject = {
  variant: "links.default",
  display: "flex",
  width: "fit-content",
  height: "fit-content",
  alignItems: "center",
  whiteSpace: "nowrap",
  svg: {
    width: "iconSizeSm",
    height: "iconSizeSm",
    flex: "none",
    ml: "s100",
  },
};

export const trailingIconGold: ThemeUIStyleObject = {
  variant: "links.trailingIcon",
  color: "textBrandLevel2",
  "&:hover": {
    color: "textPrimary",
  },
  "&:active": {
    color: "textBrand",
  },
  svg: {
    color: "textBrand",
    width: "iconSizeSm",
    height: "iconSizeSm",
    flex: "none",
    ml: "s100",
  },
};
